import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Trophy, TrendingUp, Users, Target, Download, Share2, FileImage, FileSpreadsheet, FileText, X, RefreshCw } from 'lucide-react';
import { doc, getDoc, onSnapshot, collection, query, orderBy, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';

const LiveLeaderboardPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('standings'); // standings, fixtures, stats
  const [showExportModal, setShowExportModal] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [recalculateSuccess, setRecalculateSuccess] = useState(false);
  const standingsTableRef = useRef(null);

  useEffect(() => {
    const fetchLeague = async () => {
      try {
        const leagueDoc = await getDoc(doc(db, 'leagues', leagueId));
        if (leagueDoc.exists()) {
          setLeague({ id: leagueDoc.id, ...leagueDoc.data() });
        } else {
          setError('League not found');
        }
      } catch (err) {
        setError('Failed to load league details');
        console.error('Error fetching league:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeague();
  }, [leagueId]);

  useEffect(() => {
    const teamsQuery = query(
      collection(db, 'teams'),
      orderBy('points', 'desc')
    );

    const unsubscribeTeams = onSnapshot(teamsQuery, (snapshot) => {
      const teamsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTeams(teamsData);
    });

    const fixturesQuery = query(collection(db, 'fixtures'));

    const unsubscribeFixtures = onSnapshot(fixturesQuery, (snapshot) => {
      const allFixtures = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Filter by leagueId in JavaScript
      const fixturesData = allFixtures.filter(f => f.leagueId === leagueId);
      setFixtures(fixturesData);
    });

    const matchesQuery = query(collection(db, 'matches'));

    const unsubscribeMatches = onSnapshot(matchesQuery, (snapshot) => {
      const allMatches = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Filter by leagueId in JavaScript
      const matchesData = allMatches.filter(m => m.leagueId === leagueId);
      setMatches(matchesData);
    });

    return () => {
      unsubscribeTeams();
      unsubscribeFixtures();
      unsubscribeMatches();
    };
  }, [leagueId]);

  const recalculateAllStandings = async () => {
    if (!window.confirm('This will recalculate all team standings from completed fixtures. Continue?')) {
      return;
    }

    setRecalculating(true);
    setRecalculateSuccess(false);

    try {
      console.log('Starting recalculation...');

      // Step 1: Reset all team stats to 0
      const resetPromises = teams.map(team => 
        updateDoc(doc(db, 'teams', team.id), {
          matchesPlayed: 0,
          wins: 0,
          losses: 0,
          points: 0,
          setsFor: 0,
          setsAgainst: 0,
          updatedAt: new Date().toISOString()
        })
      );
      await Promise.all(resetPromises);
      console.log('✓ Reset all team stats');

      // Step 2: Get all completed fixtures
      const fixturesSnapshot = await getDocs(collection(db, 'fixtures'));
      const completedFixtures = fixturesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(f => 
          f.status === 'completed' && 
          f.sets && 
          !f.isPlayoff // do not let playoff games change league standings
        );

      console.log(`Found ${completedFixtures.length} completed fixtures`);

      // Step 3: Process each completed fixture
      for (const fixture of completedFixtures) {
        if (!fixture.homeTeam || !fixture.awayTeam || !fixture.sets) continue;

        // Calculate sets won
        const homeSetsWon = fixture.sets.filter(s => s.completed && s.homeScore > s.awayScore).length;
        const awaySetsWon = fixture.sets.filter(s => s.completed && s.awayScore > s.homeScore).length;

        // Calculate points (1 per set + 1 bonus for match winner)
        let homePoints = homeSetsWon;
        let awayPoints = awaySetsWon;

        if (homeSetsWon >= 2) {
          homePoints += 1;
        } else if (awaySetsWon >= 2) {
          awayPoints += 1;
        }

        const homeWon = homeSetsWon >= 2;
        const awayWon = awaySetsWon >= 2;

        // Find teams
        const homeTeam = teams.find(t => t.name === fixture.homeTeam);
        const awayTeam = teams.find(t => t.name === fixture.awayTeam);

        if (!homeTeam || !awayTeam) continue;

        // Update home team
        const homeTeamDoc = await getDoc(doc(db, 'teams', homeTeam.id));
        const homeData = homeTeamDoc.data();
        await updateDoc(doc(db, 'teams', homeTeam.id), {
          matchesPlayed: (homeData.matchesPlayed || 0) + 1,
          points: (homeData.points || 0) + homePoints,
          wins: homeWon ? (homeData.wins || 0) + 1 : (homeData.wins || 0),
          losses: awayWon ? (homeData.losses || 0) + 1 : (homeData.losses || 0),
          setsFor: (homeData.setsFor || 0) + homeSetsWon,
          setsAgainst: (homeData.setsAgainst || 0) + awaySetsWon,
          updatedAt: new Date().toISOString()
        });

        // Update away team
        const awayTeamDoc = await getDoc(doc(db, 'teams', awayTeam.id));
        const awayData = awayTeamDoc.data();
        await updateDoc(doc(db, 'teams', awayTeam.id), {
          matchesPlayed: (awayData.matchesPlayed || 0) + 1,
          points: (awayData.points || 0) + awayPoints,
          wins: awayWon ? (awayData.wins || 0) + 1 : (awayData.wins || 0),
          losses: homeWon ? (awayData.losses || 0) + 1 : (awayData.losses || 0),
          setsFor: (awayData.setsFor || 0) + awaySetsWon,
          setsAgainst: (awayData.setsAgainst || 0) + homeSetsWon,
          updatedAt: new Date().toISOString()
        });

        console.log(`✓ ${fixture.homeTeam} ${homeSetsWon}-${awaySetsWon} ${fixture.awayTeam}`);
      }

      console.log(`✅ Recalculated standings for ${completedFixtures.length} matches!`);
      setRecalculateSuccess(true);
      setTimeout(() => setRecalculateSuccess(false), 3000);

    } catch (error) {
      console.error('Error recalculating standings:', error);
      alert('Failed to recalculate standings. Check console for details.');
    } finally {
      setRecalculating(false);
    }
  };

  const calculateStandings = () => {
    return teams
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.wins !== a.wins) return b.wins - a.wins;
        return a.losses - b.losses;
      })
      .map((team, index) => ({
        ...team,
        position: index + 1,
        winPercentage: team.matchesPlayed > 0 ? ((team.wins / team.matchesPlayed) * 100).toFixed(1) : 0
      }));
  };

  const getUpcomingFixtures = () => {
    const today = new Date().toISOString().split('T')[0];
    return fixtures
      .filter(f => f.date >= today && f.status === 'scheduled')
      .slice(0, 5);
  };

  const getRecentResults = () => {
    return matches.slice(0, 5);
  };

  const getLiveMatches = () => {
    const liveFixtures = fixtures.filter(f => f.status === 'live');
    console.log('All fixtures:', fixtures);
    console.log('Live fixtures:', liveFixtures);
    return liveFixtures;
  };

  const exportToImage = async () => {
    if (!standingsTableRef.current) return;
    
    try {
      // Create a container with OffCourt branding
      const exportContainer = document.createElement('div');
      exportContainer.style.cssText = `
        background: linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%);
        padding: 40px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        width: 1400px;
      `;
      
      // Add OffCourt header
      const header = document.createElement('div');
      header.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 20px;
        margin-bottom: 40px;
        padding: 30px;
        background: linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%);
        border-radius: 20px;
      `;
      
      // Add OffCourt logo/icon
      const logoDiv = document.createElement('div');
      logoDiv.style.cssText = `
        width: 80px;
        height: 80px;
        background: white;
        border-radius: 15px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
      `;
      logoDiv.innerHTML = `
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#1C563D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
          <path d="M4 22h16"></path>
          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
          <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
        </svg>
      `;
      
      const headerText = document.createElement('div');
      headerText.style.cssText = `
        text-align: center;
        color: white;
      `;
      headerText.innerHTML = `
        <div style="font-size: 48px; font-weight: bold; margin-bottom: 10px;">OffCourt</div>
        <div style="font-size: 28px; font-weight: 600;">${league?.name || 'League'}</div>
        <div style="font-size: 18px; opacity: 0.9; margin-top: 8px;">Official League Standings</div>
      `;
      
      header.appendChild(logoDiv);
      header.appendChild(headerText);
      exportContainer.appendChild(header);
      
      // Clone the standings table
      const tableClone = standingsTableRef.current.cloneNode(true);
      
      // Add crossorigin attribute to images for CORS
      const images = tableClone.getElementsByTagName('img');
      Array.from(images).forEach((img) => {
        if (img.src && img.src.includes('firebasestorage')) {
          img.setAttribute('crossorigin', 'anonymous');
        }
      });
      
      exportContainer.appendChild(tableClone);
      
      // Add footer with branding
      const footer = document.createElement('div');
      footer.style.cssText = `
        margin-top: 30px;
        padding-top: 20px;
        border-top: 2px solid #E5E7EB;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 14px;
        color: #6B7280;
      `;
      footer.innerHTML = `
        <div>Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
        <div style="display: flex; align-items: center; gap: 8px;">
          <span>Powered by</span>
          <span style="font-weight: 600; color: #1C563D;">ThinkinCode</span>
        </div>
      `;
      exportContainer.appendChild(footer);
      
      // Temporarily append to body
      exportContainer.style.position = 'absolute';
      exportContainer.style.left = '-9999px';
      exportContainer.style.top = '0';
      document.body.appendChild(exportContainer);
      
      // Small delay to ensure images load
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Capture with html2canvas with CORS enabled
      const canvas = await html2canvas(exportContainer, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: false
      });
      
      // Remove temporary element
      document.body.removeChild(exportContainer);
      
      // Download
      const link = document.createElement('a');
      link.download = `${league?.name}_standings_${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting to image:', error);
      alert('Failed to export image. Please try again.');
    }
  };

  const exportToExcel = () => {
    const standings = calculateStandings();
    
    const data = [
      ['OffCourt League Standings'],
      [league?.name],
      ['Generated on: ' + new Date().toLocaleDateString()],
      [],
      ['Position', 'Team', 'Points', 'Sets For', 'Sets Against', 'Set Difference', 'Played', 'Wins', 'Losses', 'W/L %'],
      ...standings.map(team => {
        const setsFor = team.setsFor || 0;
        const setsAgainst = team.setsAgainst || 0;
        const setDifference = setsFor - setsAgainst;
        const winPercentage = team.matchesPlayed > 0 ? ((team.wins / team.matchesPlayed) * 100).toFixed(1) : '0.0';
        
        return [
          team.position,
          team.name,
          team.points || 0,
          setsFor,
          setsAgainst,
          setDifference,
          team.matchesPlayed || 0,
          team.wins || 0,
          team.losses || 0,
          winPercentage + '%'
        ];
      }),
      [],
      ['Powered by ThinkinCode']
    ];

    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 10 },
      { wch: 20 },
      { wch: 10 },
      { wch: 12 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
      { wch: 8 },
      { wch: 8 },
      { wch: 10 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Standings');
    
    XLSX.writeFile(wb, `${league?.name}_standings_${new Date().toISOString().split('T')[0]}.xlsx`);
    setShowExportModal(false);
  };

  const exportToPDF = () => {
    // For PDF, we'll use the image export and let the user print as PDF
    window.print();
    setShowExportModal(false);
  };

  const exportLeaderboard = () => {
    setShowExportModal(true);
  };

  const shareLeaderboard = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${league?.name} Leaderboard`,
          text: `Check out the current standings for ${league?.name}`,
          url: window.location.href
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback to copying URL
      navigator.clipboard.writeText(window.location.href);
      alert('Leaderboard URL copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="loading">Loading leaderboard...</div>
      </div>
    );
  }

  if (error && !league) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#1C563D' }}>
            {error}
          </h2>
          <button
            onClick={() => navigate('/league')}
            className="btn btn-primary"
          >
            Back to League
          </button>
        </div>
      </div>
    );
  }

  const standings = calculateStandings();
  const upcomingFixtures = getUpcomingFixtures();
  const recentResults = getRecentResults();
  const liveMatches = getLiveMatches();

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F8FFFE 0%, #F0FDF4 50%, #ECFDF5 100%)',
      padding: '2rem 0'
    }}>
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: '2rem' }}
        >
          <button
            onClick={() => navigate('/league')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
              color: '#1C563D',
              border: '2px solid #E2E8F0',
              borderRadius: '0.75rem',
              fontWeight: '600',
              fontSize: '0.875rem',
              transition: 'all 0.3s ease',
              marginBottom: '2rem',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1C563D';
              e.currentTarget.style.background = 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E2E8F0';
              e.currentTarget.style.background = 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <ArrowLeft size={20} />
            Back to League
          </button>
          
          {/* Professional Header */}
          <div style={{ 
            background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
            borderRadius: '1.5rem',
            padding: '3rem 2rem',
            marginBottom: '2rem',
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '2rem' }}>
                <div>
                  <h1 style={{ 
                    fontSize: '3rem', 
                    fontWeight: 'bold', 
                    marginBottom: '1rem',
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #E5E7EB 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    {league?.name} Leaderboard
                  </h1>
                  <p style={{ 
                    fontSize: '1.25rem', 
                    opacity: 0.9,
                    marginBottom: '1.5rem'
                  }}>
                    Real-time league standings and comprehensive match analytics
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Trophy size={20} style={{ color: '#FCD34D' }} />
                      <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Teams: {teams.length}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Target size={20} style={{ color: '#60A5FA' }} />
                      <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Fixtures: {fixtures.length}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <TrendingUp size={20} style={{ color: '#34D399' }} />
                      <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Live Matches: {liveMatches.length}</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={exportLeaderboard}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '1rem 1.5rem',
                      background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                      color: '#1C563D',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '1rem',
                      fontWeight: '600',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <Download size={20} />
                    Export
                  </button>
                  <button
                    onClick={shareLeaderboard}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '1rem 1.5rem',
                      background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                      color: '#1C563D',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '1rem',
                      fontWeight: '600',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <Share2 size={20} />
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

      {/* View Mode Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <div className="flex gap-2">
          {[
            { id: 'standings', label: 'Standings', icon: Trophy },
            { id: 'fixtures', label: 'Fixtures', icon: Target },
            { id: 'stats', label: 'Stats', icon: TrendingUp }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setViewMode(id)}
              className={`btn flex items-center gap-2 ${
                viewMode === id ? 'btn-primary' : 'btn-secondary'
              }`}
            >
              <Icon size={20} />
              {label}
            </button>
          ))}
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {viewMode === 'standings' && (
          <motion.div
            key="standings"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {/* Recalculate Standings Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              style={{
                background: recalculateSuccess 
                  ? 'linear-gradient(135deg, #D1FAE5 0%, #6EE7B7 100%)'
                  : 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                border: recalculateSuccess ? '2px solid #10B981' : '2px solid #F59E0B',
                borderRadius: '1rem',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '1.125rem', 
                    fontWeight: 'bold', 
                    color: recalculateSuccess ? '#065F46' : '#92400E',
                    marginBottom: '0.5rem'
                  }}>
                    {recalculateSuccess ? '✅ Standings Recalculated!' : '⚙️ Recalculate Standings'}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: recalculateSuccess ? '#065F46' : '#92400E' }}>
                    {recalculateSuccess 
                      ? 'All team stats have been updated from completed fixtures.'
                      : 'Update all team statistics from completed fixtures (points, wins, losses, sets for/against)'}
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: recalculating ? 1 : 1.05 }}
                  whileTap={{ scale: recalculating ? 1 : 0.95 }}
                  onClick={recalculateAllStandings}
                  disabled={recalculating}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '1rem 2rem',
                    background: recalculating 
                      ? 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)'
                      : 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.75rem',
                    fontWeight: '700',
                    fontSize: '1rem',
                    cursor: recalculating ? 'not-allowed' : 'pointer',
                    boxShadow: recalculating ? 'none' : '0 4px 12px rgba(28, 86, 61, 0.3)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <RefreshCw 
                    size={20} 
                    style={{ 
                      transform: recalculating ? 'rotate(0deg)' : 'none',
                      transition: 'transform 0.5s linear'
                    }}
                    className={recalculating ? 'animate-spin' : ''}
                  />
                  {recalculating ? 'Recalculating...' : 'Recalculate Now'}
                </motion.button>
              </div>
            </motion.div>

            {/* Live Matches Alert */}
            {liveMatches.length > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50 border border-red-200 rounded-lg p-4"
              >
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse mr-3"></div>
                  <h3 className="font-semibold text-red-800">Live Matches</h3>
                </div>
                <div className="mt-2 space-y-1">
                  {liveMatches.map(match => (
                    <div key={match.id} className="text-sm text-red-700 flex items-center gap-2 flex-wrap">
                      <span>{match.homeTeam} vs {match.awayTeam} - {match.court}</span>
                      {match.isPlayoff && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-600">
                          Playoff · {match.playoffRound || 'Knockout'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Professional Standings Table */}
            <motion.div
              ref={standingsTableRef}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              style={{
                background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
                borderRadius: '1.5rem',
                padding: '2rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(28, 86, 61, 0.1)'
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center', 
                marginBottom: '2rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                    borderRadius: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 10px 15px -3px rgba(28, 86, 61, 0.3)'
                  }}>
                    <Trophy size={28} style={{ color: 'white' }} />
                  </div>
                  <div>
                    <h2 style={{ 
                      fontSize: '1.75rem', 
                      fontWeight: 'bold', 
                      color: '#1C563D',
                      marginBottom: '0.25rem'
                    }}>
                      League Standings
                    </h2>
                    <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                      Current season rankings and statistics
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#9CA3AF' }}>
                  <span>Powered by</span>
                  <img 
                    src="/thinkincode-emblem.png" 
                    alt="ThinkinCode" 
                    style={{ width: '16px', height: '16px', objectFit: 'contain' }} 
                  />
                  <span style={{ fontWeight: '600', color: '#6B7280' }}>ThinkinCode</span>
                </div>
              </div>

              <div style={{ 
                overflowX: 'auto',
                borderRadius: '1rem',
                border: '1px solid #E5E7EB',
                background: 'white'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ 
                      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                      borderBottom: '2px solid #E5E7EB'
                    }}>
                      <th style={{ 
                        padding: '1rem 1.5rem', 
                        textAlign: 'left', 
                        fontWeight: '600', 
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '60px'
                      }}>Pos</th>
                      <th style={{ 
                        padding: '1rem 1.5rem', 
                        textAlign: 'left', 
                        fontWeight: '600', 
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '200px'
                      }}>Team</th>
                      <th style={{ 
                        padding: '1rem 1.5rem', 
                        textAlign: 'center', 
                        fontWeight: '600', 
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '80px'
                      }}>Pts</th>
                      <th style={{ 
                        padding: '1rem 1.5rem', 
                        textAlign: 'center', 
                        fontWeight: '600', 
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '80px'
                      }}>Sets For</th>
                      <th style={{ 
                        padding: '1rem 1.5rem', 
                        textAlign: 'center', 
                        fontWeight: '600', 
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '90px'
                      }}>Sets Against</th>
                      <th style={{ 
                        padding: '1rem 1.5rem', 
                        textAlign: 'center', 
                        fontWeight: '600', 
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '100px'
                      }}>Set Diff</th>
                      <th style={{ 
                        padding: '1rem 1.5rem', 
                        textAlign: 'center', 
                        fontWeight: '600', 
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '80px'
                      }}>Played</th>
                      <th style={{ 
                        padding: '1rem 1.5rem', 
                        textAlign: 'center', 
                        fontWeight: '600', 
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '70px'
                      }}>W</th>
                      <th style={{ 
                        padding: '1rem 1.5rem', 
                        textAlign: 'center', 
                        fontWeight: '600', 
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '70px'
                      }}>L</th>
                      <th style={{ 
                        padding: '1rem 1.5rem', 
                        textAlign: 'center', 
                        fontWeight: '600', 
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '80px'
                      }}>W/L%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.map((team, index) => {
                      const setsFor = team.setsFor || 0;
                      const setsAgainst = team.setsAgainst || 0;
                      const setDifference = setsFor - setsAgainst;
                      const winPercentage = team.matchesPlayed > 0 ? ((team.wins / team.matchesPlayed) * 100).toFixed(1) : '0.0';
                      
                      return (
                        <motion.tr
                          key={team.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          style={{
                            borderBottom: '1px solid #F3F4F6',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #F0FDF4 0%, #F8FAFC 100%)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = index < 3 ? 'linear-gradient(135deg, #FEF3C7 0%, #FEF7CD 100%)' : 'transparent';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <td style={{ padding: '1.25rem 1.5rem' }}>
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              background: index === 0 ? 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' :
                                          index === 1 ? 'linear-gradient(135deg, #D1D5DB 0%, #9CA3AF 100%)' :
                                          index === 2 ? 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' :
                                          'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                              color: index < 3 ? 'white' : '#374151',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}>
                              {index + 1}
                            </div>
                          </td>
                          <td style={{ padding: '1.25rem 1.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              <div style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '0.75rem',
                                background: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid #E5E7EB',
                                overflow: 'hidden'
                              }}>
                                {team.logo ? (
                                  <img 
                                    src={team.logo} 
                                    alt={team.name}
                                    style={{ 
                                      width: '100%', 
                                      height: '100%', 
                                      objectFit: 'cover' 
                                    }} 
                                  />
                                ) : (
                                  <Trophy size={24} style={{ color: '#9CA3AF' }} />
                                )}
                              </div>
                              <div>
                                <div style={{ 
                                  fontWeight: '600', 
                                  fontSize: '1rem', 
                                  color: '#1F2937',
                                  marginBottom: '0.25rem'
                                }}>
                                  {team.name}
                                </div>
                                <div style={{ 
                                  fontSize: '0.875rem', 
                                  color: '#6B7280' 
                                }}>
                                  {team.category || 'Team'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ 
                            padding: '1.25rem 1.5rem', 
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '1.25rem',
                            color: '#1C563D'
                          }}>
                            {team.points || 0}
                          </td>
                          <td style={{ 
                            padding: '1.25rem 1.5rem', 
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#059669'
                          }}>
                            {setsFor}
                          </td>
                          <td style={{ 
                            padding: '1.25rem 1.5rem', 
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#DC2626'
                          }}>
                            {setsAgainst}
                          </td>
                          <td style={{ 
                            padding: '1.25rem 1.5rem', 
                            textAlign: 'center',
                            fontWeight: '600',
                            color: setDifference > 0 ? '#059669' : setDifference < 0 ? '#DC2626' : '#6B7280',
                            background: setDifference > 0 ? 'rgba(5, 150, 105, 0.1)' : setDifference < 0 ? 'rgba(220, 38, 38, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                            borderRadius: '0.5rem'
                          }}>
                            {setDifference > 0 ? '+' : ''}{setDifference}
                          </td>
                          <td style={{ 
                            padding: '1.25rem 1.5rem', 
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#374151'
                          }}>
                            {team.matchesPlayed || 0}
                          </td>
                          <td style={{ 
                            padding: '1.25rem 1.5rem', 
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#059669'
                          }}>
                            {team.wins || 0}
                          </td>
                          <td style={{ 
                            padding: '1.25rem 1.5rem', 
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#DC2626'
                          }}>
                            {team.losses || 0}
                          </td>
                          <td style={{ 
                            padding: '1.25rem 1.5rem', 
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#1C563D',
                            background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)',
                            borderRadius: '0.5rem'
                          }}>
                            {winPercentage}%
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}

        {viewMode === 'fixtures' && (
          <motion.div
            key="fixtures"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {/* Upcoming Fixtures */}
            <div className="card">
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#1C563D' }}>
                Upcoming Fixtures
              </h3>
              <div className="space-y-3">
                {upcomingFixtures.map((fixture, index) => (
                  <motion.div
                    key={fixture.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      <span>{fixture.homeTeam} vs {fixture.awayTeam}</span>
                      {fixture.isPlayoff && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                          Playoff · {fixture.playoffRound || 'TBD'}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {new Date(fixture.date).toLocaleDateString()} at {fixture.time} • {fixture.court}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Recent Results */}
            <div className="card">
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#1C563D' }}>
                Recent Results
              </h3>
              <div className="space-y-3">
                {recentResults.map((match, index) => (
                  <motion.div
                    key={match.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      <span>{match.homeTeam} vs {match.awayTeam}</span>
                      {match.isPlayoff && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                          Playoff · {match.playoffRound || 'TBD'}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {match.finalScore} • {new Date(match.completedAt).toLocaleDateString()}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {viewMode === 'stats' && (
          <motion.div
            key="stats"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <div className="card text-center">
              <Trophy className="w-12 h-12 mx-auto mb-2" style={{ color: '#1C563D' }} />
              <h3 className="text-2xl font-bold" style={{ color: '#1C563D' }}>
                {standings.length}
              </h3>
              <p className="text-gray-600">Teams</p>
            </div>
            
            <div className="card text-center">
              <Target className="w-12 h-12 mx-auto mb-2" style={{ color: '#1C563D' }} />
              <h3 className="text-2xl font-bold" style={{ color: '#1C563D' }}>
                {fixtures.length}
              </h3>
              <p className="text-gray-600">Total Fixtures</p>
            </div>
            
            <div className="card text-center">
              <TrendingUp className="w-12 h-12 mx-auto mb-2" style={{ color: '#1C563D' }} />
              <h3 className="text-2xl font-bold" style={{ color: '#1C563D' }}>
                {matches.length}
              </h3>
              <p className="text-gray-600">Matches Completed</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Export Modal */}
      <AnimatePresence>
        {showExportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '1rem'
            }}
            onClick={() => setShowExportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
                borderRadius: '1.5rem',
                padding: '2rem',
                maxWidth: '500px',
                width: '100%',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                border: '1px solid rgba(28, 86, 61, 0.1)'
              }}
            >
              {/* Modal Header */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1.5rem' 
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                    borderRadius: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Download size={20} style={{ color: 'white' }} />
                  </div>
                  <div>
                    <h3 style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: 'bold', 
                      color: '#1C563D',
                      marginBottom: '0.25rem'
                    }}>
                      Export Standings
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                      Choose your preferred format
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowExportModal(false)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '0.5rem',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F3F4F6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <X size={20} style={{ color: '#6B7280' }} />
                </button>
              </div>

              {/* Export Options */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Image Export */}
                <button
                  onClick={exportToImage}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1.25rem',
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
                    border: '2px solid #E5E7EB',
                    borderRadius: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1C563D';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(28, 86, 61, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
                    borderRadius: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FileImage size={24} style={{ color: '#1E40AF' }} />
                  </div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ fontWeight: '600', fontSize: '1rem', color: '#1F2937', marginBottom: '0.25rem' }}>
                      Export as Image
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                      High-quality PNG format
                    </div>
                  </div>
                </button>

                {/* Excel Export */}
                <button
                  onClick={exportToExcel}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1.25rem',
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
                    border: '2px solid #E5E7EB',
                    borderRadius: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1C563D';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(28, 86, 61, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)',
                    borderRadius: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FileSpreadsheet size={24} style={{ color: '#065F46' }} />
                  </div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ fontWeight: '600', fontSize: '1rem', color: '#1F2937', marginBottom: '0.25rem' }}>
                      Export to Excel
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                      Spreadsheet with all data
                    </div>
                  </div>
                </button>

                {/* PDF Export */}
                <button
                  onClick={exportToPDF}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1.25rem',
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
                    border: '2px solid #E5E7EB',
                    borderRadius: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1C563D';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(28, 86, 61, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
                    borderRadius: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FileText size={24} style={{ color: '#991B1B' }} />
                  </div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ fontWeight: '600', fontSize: '1rem', color: '#1F2937', marginBottom: '0.25rem' }}>
                      Print/Save as PDF
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                      Professional document format
                    </div>
                  </div>
                </button>
              </div>

              {/* Footer */}
              <div style={{ 
                marginTop: '1.5rem', 
                paddingTop: '1.5rem', 
                borderTop: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                fontSize: '0.75rem',
                color: '#9CA3AF'
              }}>
                <span>Powered by</span>
                <img 
                  src="/thinkincode-emblem.png" 
                  alt="ThinkinCode" 
                  style={{ width: '14px', height: '14px', objectFit: 'contain' }} 
                />
                <span style={{ fontWeight: '600', color: '#6B7280' }}>ThinkinCode</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default LiveLeaderboardPage;
